import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { List, Spin, Button, Row, Col } from 'antd';
import pluralize from 'pluralize';

import BaseAlert from 'components/wrappers/BaseAlert';
import BasePageCard from 'components/wrappers/BasePageCard';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { DashboardCustomerResponseModel } from 'api/adminApiClient';
import { getPendingCustomerRequests } from 'redux/reducers/dashboardSlice';
import { AdminRequestsSections } from 'features/admin/request/RequestsPage/enums';
import { useAppDispatch } from 'hooks/useAppDispatch';

const noPendningCustomerRequestsMessage = 'No pending customer requests.';

export default function PendingCustomerRequests() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [pendingCustomerRequests, setPendingCustomerRequests] =
    useState<DashboardCustomerResponseModel>();

  const loadPendingCustomerRequests = useCallback(async () => {
    setIsLoading(true);
    const result = await dispatch(getPendingCustomerRequests());
    setIsLoading(false);

    if (getPendingCustomerRequests.fulfilled.match(result)) {
      setPendingCustomerRequests(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  }, [dispatch]);

  useEffect(() => {
    loadPendingCustomerRequests();
  }, [loadPendingCustomerRequests]);

  const handleMoreClick = () => {
    navigate(`/todo?section=${AdminRequestsSections.Clients}`);
  };

  if (isLoading) return <Spin />;

  if (!pendingCustomerRequests) return null;

  const pluralizedCustomerRequests = pluralize(
    'requests',
    pendingCustomerRequests.totalCount,
    true,
  );

  return (
    <BasePageCard
      className="full-header stretch-card wrap-header"
      title="Pending Customer Requests"
      subTitle={pluralizedCustomerRequests}
      icons={<span className="icon icon-customer-clock" />}
    >
      {!!pendingCustomerRequests.details?.length ? (
        <>
          <List
            dataSource={pendingCustomerRequests.details}
            renderItem={({ customerCompanyName, customerKey }) => (
              <List.Item>
                <Row gutter={[2, 2]}>
                  <Col xs={24} lg={12}>
                    <Link className="card-link" to={`/customer/${customerKey}`}>
                      {customerCompanyName}
                    </Link>
                  </Col>

                  <Col xs={24} lg={12}>
                    <div className="overflow-ellipsis">{customerKey}</div>
                  </Col>
                </Row>
              </List.Item>
            )}
          />

          <div className="base-card-footer">
            <Button type="primary" onClick={handleMoreClick}>
              More
            </Button>
          </div>
        </>
      ) : (
        <span className="no-data-msg">{noPendningCustomerRequestsMessage}</span>
      )}
    </BasePageCard>
  );
}
